'use client';
import { useState, useEffect } from 'react';
import { Button, Dropdown, MenuProps, Space } from 'antd';
import { useAppContext } from '@/app/core/AppContext';
import { proxy } from '@/lib/api/proxy';

interface Customer {
  id: number;
  name: string;
}

interface User {
  name: string;
  currentCustomerId: number;
  userCustomers?: Customer[];
  customer?: Customer;
}

interface ProfileProps {
  mainportal?: boolean;
}

export function Profile({ mainportal = false }: ProfileProps) {
  const context = useAppContext();
  const user = context?.me?.user as User | undefined;
  const [selected, setSelected] = useState<Customer | undefined>();
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    function refresh() {
      if (user && user.userCustomers && user.currentCustomerId) {
        const current = user.userCustomers.find((c) => c.id === user.currentCustomerId);
        if (current) {
          setSelected(current);
        }
      }
    }

    refresh();
  }, [user]);

  function isOtherCustomer(user?: User): boolean {
    if (user && user.userCustomers && selected?.id) {
      return !user.userCustomers.some((c) => c.id === selected?.id);
    }
    return true;
  }

  function hasMultiCustomers(user?: User): boolean {
    return !!(user && user.userCustomers && user.userCustomers.length > 1);
  }

  async function handleChange(customer: Customer) {
    if (switching || !customer.id) return;

    setSwitching(true);
    setSelected(customer);

    try {
      // Call the API to switch customer
      const response = await fetch(proxy('sso', 'api/admin/switchCustomer?id=' + customer.id), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to switch customer');
      }

      // Refresh authentication and user data
      const meResponse = await fetch('/api/proxy/sso/sso/me', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (meResponse.ok) {
        // Perform a hard refresh to update the UI with the new customer context
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Error switching customer:', error);
      // Optionally show error notification
    } finally {
      setSwitching(false);
    }
  }

  if (!user) {
    return null;
  }

  const items: MenuProps['items'] = user.userCustomers?.map((customer) => ({
    key: customer.id,
    label: customer.name,
    onClick: () => handleChange(customer),
  }));

  if (!mainportal) {
    return (
      <div className="profile">
        <span>{user.name}</span>
        {isOtherCustomer(user) && (
          <span>
            {' '}
            (<i className="fa-regular fa-eye"></i> <span>{user.customer?.name}</span> )
          </span>
        )}
        {!isOtherCustomer(user) && hasMultiCustomers(user) && (
          <span>
            (<span>{user.customer?.name}</span>)
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="profile">
      <Dropdown menu={{ items }} trigger={['click']} disabled={!hasMultiCustomers(user)}>
        <Button className="profile-dropdown">
          <Space>
            <span>{user.name}</span>
            {!isOtherCustomer(user) && hasMultiCustomers(user) && (
              <span>
                (<span>{user.customer?.name}</span>)
              </span>
            )}
            {isOtherCustomer(user) && (
              <span>
                {' '}
                (<i className="fa-regular fa-eye"></i> <span>{user.customer?.name}</span> )
              </span>
            )}
            {hasMultiCustomers(user) && (
              <>
                <i className="fa-solid fa-caret-down"></i>
              </>
            )}
          </Space>
        </Button>
      </Dropdown>
    </div>
  );
}
